import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  StopCircleIcon,
  VideoCameraIcon,
  UsersIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DeleteConfirmation from "../../../components/common/DeleteConfirmation";
import Modal from "../../../components/common/Modal";
import { liveService } from "../services/liveService";
import { baseURL } from "../../../lib/axios";

const STATUS_TABS = [
  { key: "", label: "Tous" },
  { key: "live", label: "En direct" },
  { key: "ended", label: "Terminés" },
];

const LiveList = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [showEndModal, setShowEndModal] = useState(false);
  const [selectedLive, setSelectedLive] = useState(null);
  const [showParticipants, setShowParticipants] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["lives", page, status],
    queryFn: () => liveService.getLives({ page, status }),
    refetchInterval: 10000, // rafraîchir régulièrement (lives en cours)
  });

  const endMutation = useMutation({
    mutationFn: (id) => liveService.endLive(id),
    onSuccess: () => {
      toast.success("Live terminé");
      setShowEndModal(false);
      setSelectedLive(null);
      refetch();
    },
    onError: () => toast.error("Échec de l'arrêt du live"),
  });

  const banMutation = useMutation({
    mutationFn: ({ liveId, userId }) =>
      liveService.banParticipant(liveId, userId),
    onSuccess: (_data, variables) => {
      toast.success("Spectateur banni");
      setParticipants((prev) =>
        prev.filter((p) => p.user_id !== variables.userId)
      );
      refetch();
    },
    onError: () => toast.error("Échec du bannissement"),
  });

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return <div className="text-red-500">Erreur lors du chargement des lives</div>;

  const lives = data?.data || [];
  const pagination = data?.meta || {};

  const handleFilter = (key) => {
    setStatus(key);
    setPage(1);
  };

  const askEnd = (live) => {
    setSelectedLive(live);
    setShowEndModal(true);
  };

  const loadParticipants = async (live) => {
    setSelectedLive(live);
    setShowParticipants(true);
    setParticipantsLoading(true);
    try {
      const res = await liveService.getParticipants(live.id);
      setParticipants(res?.data || []);
    } catch {
      toast.error("Impossible de charger les spectateurs");
      setParticipants([]);
    } finally {
      setParticipantsLoading(false);
    }
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleString("fr-FR") : "—";

  return (
    <div className="bg-white shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Lives</h2>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleFilter(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-md transition ${
                status === tab.key
                  ? "bg-white shadow text-primary font-medium"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Live</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Boutique</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Spectateurs</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Statut</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Démarré</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">Terminé</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {lives.length > 0 ? (
              lives.map((live) => (
                <tr key={live.id}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {live.thumbnail ? (
                        <img
                          src={`${baseURL}/storage/${live.thumbnail}`}
                          alt=""
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <VideoCameraIcon className="h-6 w-6 text-primary" />
                        </div>
                      )}
                      <span className="text-sm font-semibold text-gray-800">
                        {live.title}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {live.shop_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {live.viewers_count}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        live.status === "live"
                          ? "bg-red-100 text-red-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {live.status === "live" && (
                        <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                      )}
                      {live.status === "live" ? "En direct" : "Terminé"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(live.started_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatDate(live.ended_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        title="Voir les spectateurs"
                        className="p-2 rounded hover:bg-primary/10 cursor-pointer"
                        onClick={() => loadParticipants(live)}
                      >
                        <UsersIcon className="h-6 w-6 text-primary" />
                      </button>
                      {live.status === "live" ? (
                        <button
                          title="Arrêter le live"
                          className="p-2 rounded hover:bg-red-100 cursor-pointer"
                          onClick={() => askEnd(live)}
                        >
                          <StopCircleIcon className="h-7 w-7 text-red-500" />
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 px-2">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">
                  Aucun live
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      <DeleteConfirmation
        isOpen={showEndModal}
        onClose={() => setShowEndModal(false)}
        onConfirm={() => selectedLive && endMutation.mutate(selectedLive.id)}
        title="Arrêter le live"
        message={`Êtes-vous sûr de vouloir arrêter le live « ${selectedLive?.title} » de ${selectedLive?.shop_name} ? Il sera coupé pour tous les spectateurs.`}
        confirmButtonText="Arrêter"
        confirmButtonColor="danger"
      />

      <Modal
        isOpen={showParticipants}
        onClose={() => setShowParticipants(false)}
      >
        <div className="text-left">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Spectateurs {selectedLive ? `— ${selectedLive.title}` : ""}
            </h3>
            <button
              onClick={() => setShowParticipants(false)}
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              ✕
            </button>
          </div>

          {participantsLoading ? (
            <LoadingSpinner />
          ) : participants.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Aucun spectateur présent
            </p>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
              {participants.map((p) => (
                <div
                  key={p.user_id}
                  className="flex items-center justify-between py-2.5"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-800">{p.name}</p>
                    {p.phone && (
                      <p className="text-xs text-gray-500">{p.phone}</p>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      banMutation.mutate({
                        liveId: selectedLive.id,
                        userId: p.user_id,
                      })
                    }
                    disabled={banMutation.isPending}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50 cursor-pointer"
                  >
                    <NoSymbolIcon className="h-4 w-4" />
                    Bannir
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LiveList;
